import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { Database } from '@/_constants/database.types';

type PaperAttempt = Database['public']['Tables']['past_paper_attempts']['Row'];
type PastPaper = Database['public']['Tables']['past_papers']['Row'];

export interface InProgressAttemptWithPaper extends PaperAttempt {
  past_papers: PastPaper;
}

const useInProgressPaperAttempt = (authId: string | undefined) => {
  const queryFn = async (): Promise<InProgressAttemptWithPaper | null> => {
    if (!authId) return null;

    // Fetch in-progress attempts with past_papers data
    const { data: attempts, error } = await supabaseClient
      .from('past_paper_attempts')
      .select(
        `
        *,
        past_papers (*)
      `
      )
      .eq('auth_id', authId)
      .eq('status', 'in_progress')
      .order('started_at', { ascending: false })
      .limit(1);

    if (error) throw error;
    if (!attempts || attempts.length === 0) return null;

    const attempt = attempts[0] as InProgressAttemptWithPaper;

    // Check if attempt has expired
    const startedAt = new Date(attempt.started_at).getTime();
    const timeAllowedMs = (attempt.past_papers.time_allowed || 0) * 60 * 1000;
    const now = Date.now();

    if (startedAt + timeAllowedMs < now) {
      // Attempt has expired - ignore it (cron job will clean up)
      return null;
    }

    return attempt;
  };

  return useQuery({
    queryKey: ['in_progress_paper_attempt', authId],
    queryFn,
    enabled: !!authId,
    refetchInterval: 60000, // Refetch every minute to check expiry
  });
};

export default useInProgressPaperAttempt;
